import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthChange, getUserDataByEmail, signOut as authSignOut, needsPasswordSetup as checkNeedsPassword } from '../services/auth.service';
import { subscribeToUserByEmail, saveUser } from '../services/rtdb/users.service';
import { getApprovedRequestByEmail, updateAccessRequest } from '../services/rtdb/accessRequests.service';
import {
  getInvitationByEmail,
  updateInvitation,
  isInvitationExpired,
} from '../services/rtdb/invitations.service';
import { useAppStore } from '../store';
import type { User } from '../types';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  needsPasswordSetup: boolean;
  setNeedsPasswordSetup: (v: boolean) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsPassword, setNeedsPassword] = useState(false);
  const { setCurrentUser, initializeListeners, unsubscribeAll } = useAppStore();

  // Initialize Firestore listeners only after user is authenticated
  useEffect(() => {
    if (user && user.status === 'approved') {
      initializeListeners();
    }

    return () => {
      unsubscribeAll();
    };
  }, [user, initializeListeners, unsubscribeAll]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser && firebaseUser.email) {
        try {
          const userData = await getUserDataByEmail(firebaseUser.email);

          if (userData && userData.status === 'approved') {
            setUser(userData);
            setCurrentUser(userData);
            setNeedsPassword(checkNeedsPassword(firebaseUser));
          } else if (userData) {
            console.warn('User not approved:', userData.status);
            await authSignOut();
            setUser(null);
            setCurrentUser(null);
          } else {
            // Check if this email was pre-approved via access request
            const approvedRequest = await getApprovedRequestByEmail(firebaseUser.email);
            if (approvedRequest && approvedRequest.approvedRole) {
              const role = approvedRequest.approvedRole;
              const newUser: import('../types').User = {
                id: firebaseUser.uid,
                name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                email: firebaseUser.email.toLowerCase(),
                isAdmin: false,
                isFamily: role === 'family',
                isFamilyMember: role === 'familyMember',
                status: 'approved',
              };
              await saveUser(newUser);
              await updateAccessRequest(approvedRequest.id, { status: 'rejected' }); // mark as used
              setUser(newUser);
              setCurrentUser(newUser);
              setNeedsPassword(checkNeedsPassword(firebaseUser));
            } else {
              // Check if this email has a pending admin invitation
              const invitation = await getInvitationByEmail(firebaseUser.email);
              if (invitation && !isInvitationExpired(invitation)) {
                const pendingUser: import('../types').User = {
                  id: firebaseUser.uid,
                  name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
                  email: firebaseUser.email.toLowerCase(),
                  isAdmin: false,
                  isFamily: invitation.role === 'family',
                  isFamilyMember: invitation.role === 'familyMember',
                  referredBy: invitation.invitedBy,
                  status: 'pending',
                };
                await saveUser(pendingUser);
                await updateInvitation(invitation.id, { status: 'accepted' });
                alert(
                  'Registrierung erfolgreich! Dein Zugang wird vom Admin geprüft. Du wirst nach der Freigabe benachrichtigt.'
                );
                await authSignOut();
                setUser(null);
                setCurrentUser(null);
              } else {
                if (invitation) {
                  // expired
                  alert('Deine Einladung ist abgelaufen. Bitte frage den Admin nach einer neuen.');
                } else {
                  console.warn('User not whitelisted');
                  alert('Dein Zugang ist nicht freigeschaltet. Bitte wende dich an einen Admin.');
                }
                await authSignOut();
                setUser(null);
                setCurrentUser(null);
              }
            }
          }
        } catch (error) {
          console.error('Error during auth setup:', error);
          setUser(null);
          setCurrentUser(null);
        }
      } else {
        setUser(null);
        setCurrentUser(null);
      }

      setFirebaseUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setCurrentUser]);

  // Listen for real-time changes to the current user's status
  useEffect(() => {
    if (!firebaseUser || !firebaseUser.email) return;

    const unsubscribe = subscribeToUserByEmail(firebaseUser.email, async (userData) => {
      if (!userData) {
        console.warn('User document deleted');
        await authSignOut();
        return;
      }

      if (userData.status === 'blocked') {
        console.warn('User was blocked');
        alert('Your account has been blocked by an administrator. You will be signed out.');
        await authSignOut();
        return;
      }

      if (userData.status === 'pending') {
        console.warn('User status changed to pending');
        alert('Your account is now pending approval.');
        await authSignOut();
        return;
      }

      setUser(userData);
      setCurrentUser(userData);
    });

    return () => unsubscribe();
  }, [firebaseUser, setCurrentUser]);

  const handleSignOut = async () => {
    await authSignOut();
    setUser(null);
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ firebaseUser, user, loading, needsPasswordSetup: needsPassword, setNeedsPasswordSetup: setNeedsPassword, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
};
